<template>
  <Component ref="el" :is="baseComponent" :to="link.path ? link.path : '/'"
    :class="{ active: isActive && !unclickable, inactiveLink: !!unclickable }" custom>
    <a v-if="isMenu" class="sidebar-menu-item" :aria-expanded="!collapsed" data-toggle="collapse"
      @click.prevent="collapseMenu">
      <template v-if="addLink">
        <span class="sidebar-mini-icon">{{ linkPrefix }}</span>
        <span class="sidebar-normal">
          {{ link.name }} <b class="caret"></b>
        </span>
      </template>
      <template v-else>
        <i v-if="link.materialIcon" class="material-icons">{{ link.materialIcon }}</i>
        <i v-else :class="link.icon"></i>
        <p>{{ link.name }} <b class="caret"></b></p>
      </template>
    </a>

    <TransitionExpand>
      <div v-if="$slots.default || isMenu" v-show="!collapsed" class="collapse show">
        <ul class="nav">
          <slot></slot>
        </ul>
      </div>
    </TransitionExpand>

    <slot name="title" v-if="children.length === 0 && !$slots.default && link.path">
      <component :to="link.path" @click="linkClick" :is="elementType(link, false)" :class="{ active: link.active }"
        :target="link.target" :href="link.path">
        <template v-if="addLink">
          <span class="sidebar-mini-icon">{{ linkPrefix }}</span>
          <span class="sidebar-normal">{{ link.name }}</span>
        </template>
        <template v-else>
          <i v-if="link.materialIcon" class="material-icons">{{ link.materialIcon }}</i>
          <i v-else :class="link.icon"></i>
          <p>{{ link.name }}</p>
        </template>
      </component>
    </slot>
  </Component>
</template>
<script lang="ts" setup>
import { TransitionExpand } from "@morev/vue-transitions";
import { NuxtLink } from '#components';
import {useSidebar} from "~/modules/common/stores/sidebar";

type Link = { active?: boolean, target?: string, name: string, path: string, isRoute?: boolean, materialIcon?: string, icon?: string, collapsed?: boolean };

const props = defineProps({
  menu: {
    type: Boolean,
    default: false,
    description:
      "Whether the item is a menu. Most of the item it's not used and should be used only if you want to override the default behavior."
  },
  unclickable: {
    type: Boolean,
    default: false,
  },
  link: {
    type: Object as () => Link,
    default: () => {
      return {
        name: '',
        path: '',
        children: []
      };
    },
    description:
      'Sidebar link. Can contain name, path, icon and other attributes. See examples for more info'
  }
});

const slots = useSlots();
const route = useRoute();
const $sidebar = useSidebar();

const children = ref<Link[]>([]);
const collapsed = ref(true);

const baseComponent = computed(() => props.menu || props.link.isRoute ? 'li' : 'li');
const linkPrefix = computed(() => props.link.name && (props.link.name.split(' ').map(word => word.substring(0, 1)).join('')));
const isMenu = computed(() => {
  if (!slots.default) {
    return false;
  }
  return slots.default().some(item => item.el?.tag.endsWith('sidebar-item'));
});
const isActive = computed(() => {
  if (route && route.path) {
    let matchingRoute = children.value.find(c =>
      route.path.startsWith(c.path)
    );
    if (matchingRoute !== undefined || route.path?.startsWith(props.link.path)) {
      return true;
    }
  }
  return false;
});

const addChild = (item: any) => {
  const index = slots.default?.().indexOf(item.$vnode);
  children.value = children.value.splice(index!, 0, item);
};

const removeChild = (item: any) => {
  const tabs = children.value;
  const index = tabs.indexOf(item);
  tabs.splice(index, 1);
};

const elementType = (link: Link, isParent = true) => {
  if (link.isRoute === false) {
    return isParent ? 'li' : 'a';
  } else {
    return NuxtLink;
  }
};

const linkAbbreviation = (name: string) => name.match(/\b(\w)/g)?.join('');

provide('addLink', addChild);
provide('removeLink', removeChild);

const addLink = inject<any>('addLink', null);
const removeLink = inject<any>('removeLink', null);
const autoClose = inject<any>('autoClose', true);


const linkClick = () => {
  if (
    autoClose &&
    $sidebar &&
    $sidebar.showSidebar === true
  ) {
    $sidebar.displaySidebar(false);
  }
};

const collapseMenu = () => collapsed.value = !collapsed.value;

const collapseSubMenu = (link: Link) => link.collapsed = !link.collapsed;

onMounted(() => {
  if (addLink) {
    // addLink(this);
  }
  if (props.link.collapsed) {
    collapsed.value = props.link.collapsed;
  }
  if (isActive.value && isMenu.value) {
    collapsed.value = false;
  }
});

const el = ref<HTMLElement | null>(null);

onUnmounted(() => {
  if (el.value && el.value.parentNode) {
    el.value.parentNode.removeChild(el.value);
  }
  if (removeLink) {
    // removeLink(this)
  }
});
</script>
<style>
.sidebar-menu-item {
  cursor: pointer;
}

.inactiveLink {
  pointer-events: none;
  cursor: default;
}
</style>
